import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import { CALENDAR_PLAN_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/CalendarPlanForm/utils/form';
import { LOAN_REPAYMENT_SCHEDULE_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/LoanRepaymentScheduleForm/utils/form';
import {
  calendarPlanPrefix,
  loanRepaymentSchedulePrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/utils/form';
import {
  INITIAL_LIABILITIES_NAMES,
  NON_CURRENT_LIABILITIES_NAMES,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/InitialLiabilitiesForm/utils/form';
import { initialLiabilitiesFormPrefix } from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';
import { sumLongTermDebtOpeningBalanceControlLimit } from 'components/BusinessToolForm/utils/financialCalculations';

import { concatWithDot } from 'helpers/string';

const THRESHOLD = 1e-3;

export const useLongTermDebtControlLimitWarning = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const totalLongTermDebtFieldName = concatWithDot(
    initialLiabilitiesFormPrefix,
    INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES,
    NON_CURRENT_LIABILITIES_NAMES.TOTAL_LONG_TERM_DEBT,
  );
  const calendarPlanTableFieldName = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.TABLE);
  const loanRepaymentTableFieldName = concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.TABLE);

  const totalLongTermDebt = useWatch({ control, name: totalLongTermDebtFieldName });
  const calendarPlanTable = useWatch({ control, name: calendarPlanTableFieldName });
  const loanRepaymentTable = useWatch({ control, name: loanRepaymentTableFieldName });

  const borrowedFunds = (calendarPlanTable?.[1] as IPeriodsTableRow | undefined) ?? initialPeriodsTableRow;
  const repaymentOfLoan = (loanRepaymentTable?.[0] as IPeriodsTableRow | undefined) ?? initialPeriodsTableRow;

  const controlParam = sumLongTermDebtOpeningBalanceControlLimit(borrowedFunds, repaymentOfLoan);
  const enteredValue = Number(totalLongTermDebt) || 0;
  // Excel 10.7: warn when D42 + E42 < 0
  const isControlViolated = enteredValue + controlParam < -THRESHOLD;
  const minRequiredValue = -controlParam;

  const fieldWarnings = useMemo(() => {
    if (!isControlViolated) {
      return undefined;
    }

    return {
      [NON_CURRENT_LIABILITIES_NAMES.TOTAL_LONG_TERM_DEBT]: t(
        'financialPlan.openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.totalLongTermDebtBelowControlLimit',
        { minValue: minRequiredValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
      ),
    };
  }, [isControlViolated, minRequiredValue, t]);

  return {
    fieldWarnings,
    controlParam,
    isControlViolated,
  };
};
